// External imports
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  DialogContent,
  FormControl,
  Modal,
  Paper,
  TextField,
  Grid,
  CircularProgress,
  withStyles,
} from '@material-ui/core';
import { pdfjs } from 'react-pdf';
import { isMobile, isMobileOnly, withOrientationChange } from 'react-device-detect';
import _ from 'lodash';
// Internal imports
import { useSetState } from '~/app/Utility/customHooks.js';
import Spinner from '#/Common/Spinner.jsx';
import AuthAPI from '~/app/api/authAPI.js';
import WebHelpers, { statusOK } from '~/app/webHelpers';
import ErrorHelpers from '~/app/errorHelpers.js';
import RemoteSignPad from './RemoteSignaturePad.jsx';
import RemoteSignPreview from './RemoteSignaturePreview.jsx';
import rotateDevice from '~/images/icons/rotateDevice.png';
import SimpleDialog from '#/Common/SimpleDialog.jsx';
import {
  SPINNER_DIALOGS,
  SIGNEE_CATEGORY_TYPE,
  SIGNEE_TYPE,
  SIGNATURE_CATEGORY,
  SIGNATURE_CATEGORY_TO_SIGNATURE_TYPE,
  ERROR_IN_DOCUMENT_CANCEL_MESSAGE,
  CONSENT_CANCEL_MESSAGE,
} from '~/app/constants.js';
import {
  buildTitleLabel,
  formHasErrors,
} from '~/app/Components/RemoteSign/helpers/remoteSignAuthHelpers.js';
import { NISTAuditLog } from '~/app/Utility/clientNISTLogger.js';
import { NIST_LOG_EVENTS } from '~/app/Utility/clientNISTConstants.js';
// Redux imports
import { useDispatch, useSelector } from 'react-redux';
import { actions as remoteSignActions } from '~/app/redux/remoteSign/duck';
// Styling imports
import '~/app/Components/RemoteSign/css/remoteSign.css';
import '~/app/Components/RemoteSign/css/remoteSignatureAuth.css';
import { styles } from '~/app/Components/RemoteSign/css/remoteSignatureAuth.js';
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const RemoteSignatureAuth = props => {
  const { classes } = props;
  const dispatch = useDispatch();
  const { isMaintenanceModeTime } = useSelector(state => ({
    isMaintenanceModeTime: state.maintenance.isMaintenanceModeTime,
  }));
  const [state, setState] = useSetState({
    form: {
      name: '',
      lastFourPrimary: '',
      day: '',
      month: '',
      year: '',
      zipCode: '',
    },
    inputErrors: {},
    isInitialVerify: true,
    isSubmitDisabled: true,
    windowHeight: '',
    windowWidth: '',
    isShowInvalidDateModalOpen: false,
    isOpenInvalidDataModalOpen: false,
    isSignatureModalOpen: false,
    isSignaturePreviewOpen: false,

    // NOTE: pdfData previously held base64. Keep it for now (backward compat / debugging),
    // but the UI now uses pdfUrl (blob URL) to support Mobile Safari reliably.
    pdfData: '',
    pdfUrl: '',

    validTest: -1,
    signatureValidationID: -1,
    signatureCategory: '',
    signatureType: '',
    signatureEmail: '',
    signatureRequests: '',
    isAuthenticationPageOpen: true,
    isPDFPageOpen: false,
    isCancelPageOpen: false,
    signatureToken: '',
    alreadySigned: -1,
    // Remote signature, person who will be signing
    isRemoteSignAuthTaxpayerSpouse: false,
    isRemoteSignAuthPreparer: false,
    isRemoteSignAuthBusiness: false,
    isRemoteSignAuthERO: false,
    hasIssueLoadingPDF: false,
    isAllDone: false,
    signatureRequestsModified: '',
    isProcessingSignature: false,
    isNeedingToRotate: false,
    cancelMessage: '',
    season: 0,
    remoteSignature: '',
    remoteSignatureString: '',
    isPDFOpen: true,
    isValidSigRequest: false,
    isRMSAuthCodeActive: true,
    isInOfficeDeviceSign: false,
    isConsentModalOpen: false,
    consent: '',
    hasConsented: false,
  });
  const [debouncedState, setDebouncedState] = useState({
    form: {
      name: '',
      lastFourPrimary: '',
      day: '',
      month: '',
      year: '',
      zipCode: '',
    },
  });

  // Track the last created blob URL so we can revoke it.
  const lastPdfUrlRef = useRef('');

  const queryParams = props.match.params.code;
  const signeeType = queryParams.substring(0, 1);
  const isInOfficeDeviceSign = queryParams.substring(1, 2) === '1';

  // verifyCode is what's left between index 2 and the tail
  const verifyCode = props.match.params.code.substring(2);

  useEffect(() => {
    getVersionInfo();

    if (isInOfficeDeviceSign) {
      toggleStateBool('isInOfficeDeviceSign');
    }

    switch (signeeType) {
      case SIGNATURE_CATEGORY.TAXPAYER:
      case SIGNATURE_CATEGORY.SPOUSE:
        toggleStateBool('isRemoteSignAuthTaxpayerSpouse');
        break;
      case SIGNATURE_CATEGORY.PREPARER:
      case SIGNATURE_CATEGORY.AMENDED_PREPARER:
        toggleStateBool('isRemoteSignAuthPreparer');
        break;
      case SIGNATURE_CATEGORY.OFFICER:
        toggleStateBool('isRemoteSignAuthBusiness');
        break;
      default:
        toggleStateBool('isRemoteSignAuthTaxpayerSpouse');
        break;
    }

    handleResize();
    window.addEventListener('resize', handleResize);

    // check to see if the auth code is active, if not, supply error message
    isRMSAuthCodeActive();

    return () => {
      window.removeEventListener('resize', handleResize);
      // Revoke any active blob URL on unmount
      if (lastPdfUrlRef.current) {
        URL.revokeObjectURL(lastPdfUrlRef.current);
        lastPdfUrlRef.current = '';
      }
    };
  }, []);

  // Handles setting timeout on form change
  const debounce = useCallback(
    _.debounce(_formVal => {
      setDebouncedState(_formVal);
      // send the server request here
    }, 3000),
    [],
  );

  // When debounce state changes after user stops typing for a set period of time, then verify return.
  useEffect(() => {
    if (state.isInitialVerify) {
      toggleStateBool('isInitialVerify');
    }

    setState({
      isSubmitDisabled: formHasErrors(
        state.form,
        handleSetErrors,
        state.isRemoteSignAuthTaxpayerSpouse,
      ),
      isVerifying: false,
    });
  }, [debouncedState, state.isInOfficeDeviceSign]);

  useEffect(() => {
    handleShouldShowConsent();
  }, [state.isSignatureModalOpen]);

  const handleShouldShowConsent = () => {
    if (
      state.consent &&
      [1, 2, 7].includes(parseInt(state.signatureType)) &&
      state.isSignatureModalOpen
    ) {
      setState({
        isConsentModalOpen: true,
      });
    } else {
      setState({
        hasConsented: true,
      });
    }
  };

  // Verifiies the auth code
  const isRMSAuthCodeActive = async () => {
    try {
      const res = await AuthAPI.IsRMSAuthCodeActive(verifyCode, signeeType);
      if (statusOK(res, false)) {
        setState({ isRMSAuthCodeActive: res?.data || false });
      }
    } catch (err) {
      ErrorHelpers.handleError('Unable to verify the RMS Auth Code: ', err);
    }
  };

  // Adding function that will handle resizing
  const handleResize = () => {
    setState({
      windowHeight: window.innerHeight - 200,
      windowWidth: window.innerWidth * 0.59,
    });
  };

  const handleSubmit = e => {
    e.preventDefault();
    // Calling API to check if data entered on screen matches the data we have saved on the remote signatures table
    getDataMatches();
  };

  const loadPdfForPreview = async (sigDocID, signatureCategory, signatureToken) => {
    try {
      // clear any previous "issue loading PDF" banner
      setState({ hasIssueLoadingPDF: false });

      // Revoke the previous blob URL if present
      if (lastPdfUrlRef.current) {
        URL.revokeObjectURL(lastPdfUrlRef.current);
        lastPdfUrlRef.current = '';
      }

      const res = await AuthAPI.GetRemoteSignPDF(sigDocID, signatureCategory, signatureToken);
      if (statusOK(res)) {
        const url = URL.createObjectURL(res.data);
        lastPdfUrlRef.current = url;

        setState({ pdfUrl: url });

        return url;
      }

      // If API request is successful, but data/blob was incorrect
      setState({ hasIssueLoadingPDF: true });
    } catch (error) {
      setState({ hasIssueLoadingPDF: true });
      ErrorHelpers.handleError('Unable to load PDF for preview', error);
      return '';
    }
  };

  const getDataMatches = async () => {
    // If the information is correct, load the signature pad component, if not show modal stating that the information provided does not match our records
    // Trimming white spaces from name and zip incase user adds spaces
    const name = state.form.name?.trim();
    const lastFourPrimary = state.form.lastFourPrimary;
    const zip = state.form.zipCode?.trim();
    // format the db wants is yyyymmdd. In most cases we are displaying as mmddyyyy
    const dob = `${state.form.year}-${state.form.month}-${state.form.day}`;

    if (state.isRemoteSignAuthTaxpayerSpouse) {
      try {
        const res = await AuthAPI.CheckRemoteSignAuthData(
          name,
          dob,
          lastFourPrimary,
          zip,
          verifyCode,
          parseInt(signeeType),
          state.isInOfficeDeviceSign,
        );

        if (statusOK(res)) {
          const data = res.data;

          // If the request isn't valid or already signed, we don't need to load the PDF
          const shouldLoadPdf =
            data.validSigRequest && data.alreadySigned === 0 && data.sigDocID > 0;

          if (data.pdfData === '') {
            // We have a blank pdf, so show error message
            toggleStateBool('isAuthenticationPageOpen', 'isOpenInvalidDataModalOpen');
          } else {
            try {
              // Set Signature needed mapping
              dispatch(remoteSignActions.signaturesNeededSet(data?.signatureRequests || ''));

              // Load PDF as a blob URL (Safari-friendly)
              let pdfUrl = '';
              if (shouldLoadPdf) {
                pdfUrl = await loadPdfForPreview(
                  data.sigDocID,
                  data.signatureCategory,
                  data.signatureToken,
                );
              }

              setState({
                pdfData: data.pdfData, // keep for now
                pdfUrl,
                validTest: data.validLoginSignDataEntered,
                signatureValidationID: data.sigValidationID,
                sigDocID: data.sigDocID,
                signatureCategory: data.signatureCategory,
                // The blob is stored under the signature type, which differs from the
                // category for the amended preparer (category 9, type 10).
                signatureType:
                  SIGNATURE_CATEGORY_TO_SIGNATURE_TYPE[data.signatureCategory] ??
                  data.signatureCategory,
                signatureEmail: data.signatureEmail,
                signatureRequests: data.signatureRequests,
                isAuthenticationPageOpen: false,
                isPDFPageOpen: true,
                signatureToken: data.signatureToken,
                alreadySigned: data.alreadySigned,
                // Remove values
                form: {
                  name: '',
                  lastFourPrimary: '',
                  day: '',
                  month: '',
                  year: '',
                  zipCode: '',
                },
                season: data.season,
                isValidSigRequest: data.validSigRequest,
                consent: data.consent,
              });
            } catch (error) {
              ErrorHelpers.handleError('Unable to retrieve signature information', error);
            }
          }
        }
      } catch (error) {
        ErrorHelpers.handleError(
          'Invalid Remote Signature Authentication Data Entered for Taxpayer/Spouse',
          error,
        );
      }
    }
    if (
      state.isRemoteSignAuthPreparer ||
      state.isRemoteSignAuthERO ||
      state.isRemoteSignAuthBusiness
    ) {
      try {
        const res = await AuthAPI.CheckRemoteSignAuthDataPrepEROBusiness(
          name,
          lastFourPrimary,
          verifyCode,
          parseInt(signeeType),
          state.isInOfficeDeviceSign,
        );
        if (statusOK(res)) {
          const data = res.data;

          const shouldLoadPdf =
            data.validSigRequest && data.alreadySigned === 0 && data.sigDocID > 0;

          if (data.pdfData === '') {
            // We have a blank pdf, so show error message
            toggleStateBool('isAuthenticationPageOpen', 'isOpenInvalidDataModalOpen');
          } else {
            try {
              // Load PDF as a blob URL (Safari-friendly)
              let pdfUrl = '';
              if (shouldLoadPdf) {
                pdfUrl = await loadPdfForPreview(
                  data.sigDocID,
                  data.signatureCategory,
                  data.signatureToken,
                );
              }

              setState({
                pdfData: data.pdfData, // keep for now
                pdfUrl,
                validTest: data.validLoginSignDataEntered,
                signatureValidationID: data.sigValidationID,
                sigDocID: data.sigDocID,
                signatureCategory: data.signatureCategory,
                signatureEmail: data.signatureEmail,
                signatureRequests: data.signatureRequests,
                isAuthenticationPageOpen: false,
                isPDFPageOpen: true,
                signatureToken: data.signatureToken,
                alreadySigned: data.alreadySigned,
                // The blob is stored under the signature type, which differs from the
                // category for the amended preparer (category 9, type 10).
                signatureType:
                  SIGNATURE_CATEGORY_TO_SIGNATURE_TYPE[data.signatureCategory] ??
                  data.signatureCategory,
                // Remove values
                form: {
                  name: '',
                  lastFourPrimary: '',
                  day: '',
                  month: '',
                  year: '',
                  zipCode: '',
                },
                season: data.season,
                isValidSigRequest: data.validSigRequest,
                consent: data.consent,
              });

              // NIST - logging the user action of adding a widget
              NISTAuditLog(
                NIST_LOG_EVENTS.REMOTE_SIGNATURE_VERIFICATION.key,
                `User is verifying their remote signature authentication - Signature Document: ${data.sigDocID}`,
              );
            } catch (error) {
              ErrorHelpers.handleError('Unable to retrieve signature information', error);
            }
          }
        }
      } catch (error) {
        ErrorHelpers.handleError(
          'Invalid Remote Signature Authentication Data Entered for Preparer/ERO/Business',
          error,
        );
      }
    }
  };

  const handleCancel = type => {
    let cancelMessage = '';

    if (type === 'Document Error') {
      cancelMessage = ERROR_IN_DOCUMENT_CANCEL_MESSAGE;
    } else if (type === 'consent') {
      cancelMessage = CONSENT_CANCEL_MESSAGE;
    }
    // Show message when user clicks on cancel button
    setState({
      cancelMessage,
    });
    toggleStateBool('isCancelPageOpen', 'isPDFPageOpen');
  };

  const getVersionInfo = async () => {
    try {
      const res = await WebHelpers.getXlinkCloudVersion();
      if (statusOK(res, false)) {
        setState({
          xlinkapiver: res,
        });
      }
    } catch (err) {
      ErrorHelpers.handleError('Error fetching xlinkcloud version', err);
    }
  };

  const postSignature = async () => {
    // Posting signature to db
    toggleStateBool('isProcessingSignature');

    try {
      const res = await AuthAPI.PostSignedSignatureData(
        state.sigDocID,
        state.signatureValidationID,
        state.signatureCategory,
        state.signatureType,
        state.remoteSignatureString,
        state.signatureToken,
        1, // Hardcoding for now: electronic disclosure has been added to 'sign_consent,' so this is no longer necessary. Users shouldn’t be able to reach this point without agreeing to the consent.
        state.season,
        state.isInOfficeDeviceSign,
        state.consent,
      );

      if (statusOK(res, false)) {
        getRemoteSignatureCount();

        // NIST - logging the user action of signing a remote signature
        NISTAuditLog(
          NIST_LOG_EVENTS.SIGNING_REMOTE_SIGNATURE.key,
          `User has signed their remote signature - signature document ID: ${state.sigDocID} - Signature Type: ${state.signatureType}`,
        );
      }
    } catch (error) {
      ErrorHelpers.handleError('Failed to Post Remote Signature Data', error);
    }
    setState({ isProcessingSignature: false });
  };

  // getRemoteSignatureCount after posting the signature
  const getRemoteSignatureCount = () => {
    let signatureRequestsCopy = state.signatureRequests;

    if (state.signatureRequestsModified.length > 0) {
      signatureRequestsCopy = state.signatureRequestsModified;
    }

    // TAXPAYER CATEGORY
    if (state.signatureCategory === SIGNEE_CATEGORY_TYPE.TAXPAYER) {
      if (signatureRequestsCopy.includes(SIGNEE_TYPE.TAXPAYER)) {
        signatureRequestsCopy = signatureRequestsCopy.replace(SIGNEE_TYPE.TAXPAYER, '');
      }

      if (signatureRequestsCopy.includes(SIGNEE_TYPE.TAXPAYER_INITIAL)) {
        signatureRequestsCopy = signatureRequestsCopy.replace(SIGNEE_TYPE.TAXPAYER_INITIAL, '');
        setState({
          isSignatureModalOpen: true,
          signatureType: SIGNEE_CATEGORY_TYPE.TAXPAYER_INITIAL,
        });
      } else if (signatureRequestsCopy.includes(SIGNEE_TYPE.TAXPAYER_SECOND_SIGNATURE)) {
        signatureRequestsCopy = signatureRequestsCopy.replace(
          SIGNEE_TYPE.TAXPAYER_SECOND_SIGNATURE,
          '',
        );
        setState({
          isSignatureModalOpen: true,
          signatureType: SIGNEE_CATEGORY_TYPE.TAXPAYER_SECOND_SIGNATURE,
        });
      } else {
        toggleStateBool('isAllDone', 'isPDFPageOpen');
      }
    }

    // SPOUSE CATEGORY
    if (state.signatureCategory === SIGNEE_CATEGORY_TYPE.SPOUSE) {
      if (signatureRequestsCopy.includes(SIGNEE_TYPE.SPOUSE)) {
        signatureRequestsCopy = signatureRequestsCopy.replace(SIGNEE_TYPE.SPOUSE, '');
      }

      if (signatureRequestsCopy.includes(SIGNEE_TYPE.SPOUSE_INITIAL)) {
        signatureRequestsCopy = signatureRequestsCopy.replace(SIGNEE_TYPE.SPOUSE_INITIAL, '');
        setState({
          isSignatureModalOpen: true,
          signatureType: SIGNEE_CATEGORY_TYPE.SPOUSE_INITIAL,
        });
      } else if (signatureRequestsCopy.includes(SIGNEE_TYPE.SPOUSE_SECOND_SIGNATURE)) {
        signatureRequestsCopy = signatureRequestsCopy.replace(
          SIGNEE_TYPE.SPOUSE_SECOND_SIGNATURE,
          '',
        );
        setState({
          isSignatureModalOpen: true,
          signatureType: SIGNEE_CATEGORY_TYPE.SPOUSE_SECOND_SIGNATURE,
        });
      } else {
        toggleStateBool('isAllDone', 'isPDFPageOpen');
      }
    }

    // ERO/PREP/AMENDED PREP/BUSINESS CATEGORY
    // These signees sign once with nothing following, so completing the signature
    // completes the session. Note this compares categories, not signature types:
    // the amended preparer's category is 9 while its type is 10, so
    // SIGNEE_CATEGORY_TYPE.AMENDED_PAID_PREPARER_SIGNATURE would not match here.
    // Without this the amended preparer never reached the completion screen.
    if (
      [
        SIGNEE_CATEGORY_TYPE.PREPARER,
        SIGNEE_CATEGORY_TYPE.ERO,
        SIGNEE_CATEGORY_TYPE.OFFICER,
        Number(SIGNATURE_CATEGORY.AMENDED_PREPARER),
      ].includes(state.signatureCategory)
    ) {
      toggleStateBool('isAllDone', 'isPDFPageOpen');
    }

    // SET REQ SIGNATURES
    setState({
      signatureRequestsModified: signatureRequestsCopy,
    });
  };

  const handleSignReturn = async () => {
    try {
      await AuthAPI.PostCodeRemoteSign(
        state.sigDocID,
        state.signatureType,
        state.signatureEmail,
        state.signatureValidationID,
      );
    } catch (error) {
      ErrorHelpers.handleError('Failed to Post Remote Signature Code', error);
    }

    if (isMobileOnly && props.isPortrait) {
      toggleStateBool('isNeedingToRotate');
    }

    toggleStateBool('isSignatureModalOpen');
  };

  const toggleStateBool = (nameOne, nameTwo = '') => {
    if (nameTwo === '') {
      setState({
        [nameOne]: !state[nameOne],
      });
    } else {
      setState({
        [nameOne]: !state[nameOne],
        [nameTwo]: !state[nameTwo],
      });
    }
  };

  /** Handles input change */
  const handleChange = e => {
    const { name, value } = e.target;

    // Fields that must be numeric only
    const numericFields = ['month', 'day', 'year', 'lastFourPrimary', 'zipCode'];

    let updatedValue = value;

    if (numericFields.includes(name)) {
      // Remove anything that is not a digit
      updatedValue = value.replace(/\D/g, '');
    } else {
      // Preserve existing behavior for other fields
      updatedValue = value.toUpperCase();
    }

    const updatedForm = {
      ...state.form,
      [name]: updatedValue,
    };

    setState({
      form: updatedForm,
      isSubmitDisabled: true,
      isVerifying: true,
    });

    debounce({
      form: updatedForm,
    });

    // Reset field-specific error if user edits it
    if (state.inputErrors?.[name]) {
      const tempErrors = { ...state.inputErrors };
      delete tempErrors[name];
      handleSetErrors(tempErrors);
    }
  };

  const handleSetErrors = errors => {
    setState({ inputErrors: errors }); // set any errors to the local state
  };

  const pdfUrl = state.pdfUrl;
  const namePlaceholder = signeeType === '7' ? 'Smith Co...' : 'Smith...';
  const nameInputLabel =
    (state.isRemoteSignAuthTaxpayerSpouse
      ? 'Last '
      : state.isRemoteSignAuthBusiness
      ? 'Business '
      : '') + 'Name';
  const isMobileStyle = state.windowWidth <= 500 && state.windowHeight < 824;
  const lastFourNumLabel =
    'Last 4 Digits of ' +
    (state.isRemoteSignAuthTaxpayerSpouse
      ? 'SSN'
      : state.isRemoteSignAuthPreparer
      ? 'SSN/PTIN' // This squishes the label, we need to increase the grid for this field
      : state.isRemoteSignAuthERO || state.isRemoteSignAuthBusiness
      ? 'EIN'
      : null);

  if (state.isNeedingToRotate && props.isPortrait) {
    return (
      <div className="remote-sign-rotate-device-block">
        <h5 className="remote-sign-rotate-device-content">
          Please Rotate Your Device to Proceed Signing the Document
        </h5>
        <img className="remote-sign-rotate-img" src={rotateDevice} />
      </div>
    );
  } else if (!state.isRMSAuthCodeActive) {
    return (
      <div className="remoteAuthenticationPage">
        <Paper classes={{ root: isMobileStyle && classes.paper }} className="remote-sign-container">
          <p>
            Access to the page has expired or is an invalid URL Code. If you believe this is a
            mistake, please reach out to your Preparer sending them a screenshot and the entire URL
            path.
          </p>
        </Paper>
      </div>
    );
  } else {
    return (
      <>
        {state.isAuthenticationPageOpen ? (
          <div className="remoteAuthenticationPage">
            <Paper
              classes={{ root: isMobileStyle && classes.paper }}
              className="remote-sign-container"
            >
              <div className="remote-sign-log-in">{buildTitleLabel(signeeType)}</div>
              <Grid container spacing={32}>
                <Grid item xs={12}>
                  <hr className="remote-sign-horizontal-line" />
                </Grid>

                {/* name is included for each signee type */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <TextField
                      id="txtNameSignLogin"
                      name="name"
                      value={state.form.name || ''}
                      label={nameInputLabel}
                      placeholder={namePlaceholder}
                      onChange={handleChange}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{ maxLength: 100 }}
                      variant="outlined"
                      autoComplete="off"
                      helperText={state.inputErrors?.name}
                      error={state.inputErrors?.name !== undefined}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={state.isRemoteSignAuthPreparer ? 12 : 6}>
                  <FormControl>
                    <TextField
                      id="nflastFourNumSignLogin"
                      name="lastFourPrimary"
                      value={state.form.lastFourPrimary || ''}
                      label={lastFourNumLabel}
                      placeholder="####"
                      onChange={handleChange}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{ maxLength: 4 }}
                      variant="outlined"
                      autoComplete="off"
                      helperText={state.inputErrors?.lastFourPrimary}
                      error={state.inputErrors?.lastFourPrimary !== undefined}
                    />
                  </FormControl>
                </Grid>

                {state.isRemoteSignAuthTaxpayerSpouse && (
                  <>
                    <Grid item xs={6}>
                      <FormControl>
                        <TextField
                          id="nfZipCodeSignLogin"
                          name="zipCode"
                          value={state.form.zipCode || ''}
                          label="Zip Code"
                          placeholder="#####"
                          onChange={handleChange}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          inputProps={{ maxLength: 14 }}
                          variant="outlined"
                          autoComplete="off"
                          helperText={state.inputErrors?.zipCode}
                          error={state.inputErrors?.zipCode !== undefined}
                        />
                      </FormControl>
                    </Grid>
                    <Grid container item xs={12} justify="space-between">
                      <Grid item xs={3}>
                        <FormControl>
                          <TextField
                            id="nfMonthSignLogin"
                            name="month"
                            value={state.form.month || ''}
                            label="DOB"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            onChange={handleChange}
                            inputProps={{ maxLength: 2 }}
                            placeholder="Month"
                            variant="outlined"
                            autoComplete="off"
                            helperText={state.inputErrors?.month}
                            error={state.inputErrors?.month !== undefined}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={3}>
                        <FormControl>
                          <TextField
                            id="nfDaySignLogin"
                            name="day"
                            value={state.form.day || ''}
                            onChange={handleChange}
                            inputProps={{ maxLength: 2 }}
                            placeholder="Day"
                            variant="outlined"
                            autoComplete="off"
                            helperText={state.inputErrors?.day}
                            error={state.inputErrors?.day !== undefined}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl>
                          <TextField
                            id="nfYearSignLogin"
                            name="year"
                            value={state.form.year || ''}
                            onChange={handleChange}
                            inputProps={{ maxLength: 4 }}
                            placeholder="Year"
                            variant="outlined"
                            autoComplete="off"
                            helperText={state.inputErrors?.year}
                            error={state.inputErrors?.year !== undefined}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Button
                      id="btnVerifySignLogin"
                      onClick={handleSubmit}
                      variant="contained"
                      type="submit"
                      color="primary"
                      disabled={state.isSubmitDisabled || isMaintenanceModeTime}
                    >
                      {state.isVerifying ? (
                        <>
                          <CircularProgress
                            size="1rem"
                            color="inherit"
                            className={classes.smallSpinner}
                          />
                          Verifying...
                        </>
                      ) : (
                        'Verify'
                      )}
                    </Button>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          </div>
        ) : null}

        {state.isPDFPageOpen && state.isValidSigRequest && state.alreadySigned === 0 && (
          <Paper className="remote-sign-download-doc-container">
            <div>
              <span className="remote-signature-dowload-container">
                {pdfUrl ? (
                  <>
                    <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                      <Button variant="contained" color="primary">
                        Open Document
                      </Button>
                    </a>
                    {/* Keep download option for browsers that support it */}
                    <span style={{ marginLeft: '10px' }}>
                      <a href={pdfUrl} download="document.pdf">
                        <Button variant="outlined" color="primary">
                          Download
                        </Button>
                      </a>
                    </span>
                  </>
                ) : (
                  <Button variant="contained" color="primary" disabled>
                    Loading Document...
                  </Button>
                )}
              </span>
              <h6>Document Preview</h6>
            </div>

            <div className="remote-sign-pdf-return-view-container">
              {!pdfUrl ? (
                <div className="remote-sign-document-block remote-sign-document-block-center">
                  <p>Loading document preview...</p>
                </div>
              ) : (
                <object
                  id="pdf"
                  className="remote-sign-pdf-return-view"
                  data={pdfUrl}
                  type="application/pdf"
                />
              )}

              {isMobile && pdfUrl && (
                <div
                  className="remote-sign-document-block remote-sign-document-block-center"
                  style={{ marginTop: '10px' }}
                >
                  <p>
                    If the preview doesn&apos;t display on your phone, tap <b>Open Document</b>{' '}
                    above to view it in your device&apos;s PDF viewer.
                  </p>
                </div>
              )}
            </div>

            <div className="remote-sign-download-doc-form-btn-container">
              <p>Please verify that the document is accurate to the best of your knowledge.</p>
              <Button
                onClick={() => handleCancel('Document Error')}
                style={{ display: 'inline-block', marginRight: '10px' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSignReturn}
                variant="contained"
                color="primary"
                style={{ display: 'inline-block' }}
              >
                Sign
              </Button>
            </div>
          </Paper>
        )}

        {/* Modals and Dialogs */}
        {!state.isValidSigRequest &&
          !state.isOpenInvalidDataModalOpen &&
          state.alreadySigned === 0 && (
            <Paper classes={{ root: classes.msgContent }}>
              <p>
                Our records indicate that this is no longer a valid remote signature request. Please
                contact your tax preparer to have them send you a new remote signature request.
                <br />
                Please close this window.
              </p>
            </Paper>
          )}
        {state.isInOfficeDeviceSign &&
          !state.isValidSigRequest &&
          !state.isOpenInvalidDataModalOpen &&
          state.alreadySigned === 0 && (
            <Paper classes={{ root: classes.msgContent }}>
              <p>
                Our records indicate that this is no longer a valid remote signature request.
                Another In-Office Device Signature request will need to be made.
                <br />
                Please close this window.
              </p>
            </Paper>
          )}
        {state.alreadySigned === 1 && (
          <Paper classes={{ root: classes.msgContent }}>
            <p>
              Our records indicate that you have already signed your return.
              <br />
              Please close this window.
            </p>
          </Paper>
        )}
        {state.isCancelPageOpen && (
          <Paper classes={{ root: classes.msgContent }}>
            <p>
              {state.cancelMessage}
              <br />
              Please close this window.
            </p>
          </Paper>
        )}
        {state.isAllDone && (
          <Paper classes={{ root: classes.msgContent }}>
            <p>
              Thank you for submitting your signature. The remote signature process has been
              completed.
              <br />
              Please close this window.
            </p>
          </Paper>
        )}
        {state.hasIssueLoadingPDF && (
          <Paper classes={{ root: classes.msgContent }}>
            <p>
              There was an issue loading your document. Try “Open Document” or refresh the page.
            </p>
          </Paper>
        )}
        {state.isSignatureModalOpen && (
          <Modal
            id="mdlRemoteSignatureModal"
            aria-labelledby="simple-modal-remote-signature"
            aria-describedby="simple-modal-remote-signature-description"
            open={state.isSignatureModalOpen}
            onClose={() => toggleStateBool('isSignatureModalOpen')}
          >
            <DialogContent classes={{ root: classes.dialogContent }}>
              <RemoteSignPad
                handlePreviewSignature={() => {
                  toggleStateBool('isSignatureModalOpen', 'isSignaturePreviewOpen');
                }}
                currentSig={state.signatureType}
                isNeedingToRotate={() => toggleStateBool('isNeedingToRotate')}
                setRemoteSignature={remoteSignature => setState({ remoteSignature })}
                setRemoteSignatureString={remoteSignatureString => {
                  setState({ remoteSignatureString });
                }}
                hasConsented={state.hasConsented}
              />
            </DialogContent>
          </Modal>
        )}
        {state.isSignaturePreviewOpen && (
          <Modal
            id="mdlRemoteSignaturePreviewModal"
            aria-labelledby="simple-modal-remote-siganture-preview"
            aria-describedby="simple-modal-remote-signature-preview"
            open={state.isSignaturePreviewOpen}
            onClose={() => toggleStateBool('isSignaturePreviewOpen')}
          >
            <DialogContent classes={{ root: classes.dialogContent }}>
              <RemoteSignPreview
                handleCloseSignatureModal={() => toggleStateBool('isSignaturePreviewOpen')}
                handleRecapture={() => {
                  toggleStateBool('isSignatureModalOpen', 'isSignaturePreviewOpen');
                }}
                postSignature={postSignature}
                isNeedingToRotate={() => toggleStateBool('isNeedingToRotate')}
                remoteSignature={state.remoteSignature}
              />
            </DialogContent>
          </Modal>
        )}
        <SimpleDialog
          open={state.isShowInvalidDateModalOpen}
          onConfirm={() => toggleStateBool('isShowInvalidDateModalOpen')}
          dialogTitle="INVALID DATE OF BIRTH"
          contentText="Please enter a valid date of birth."
          styled
        />
        <SimpleDialog
          open={state.isOpenInvalidDataModalOpen}
          onConfirm={() =>
            toggleStateBool('isOpenInvalidDataModalOpen', 'isAuthenticationPageOpen')
          }
          dialogTitle="PLEASE VERIFY YOUR INFORMATION"
          contentText="The information provided does not match our records. If you believe the information
          provided is correct, please contact your tax preparer."
          styled
        />

        {state.isConsentModalOpen && (
          <SimpleDialog
            open={state.isConsentModalOpen}
            confirmText="OK"
            cancelText="Cancel"
            styled={true}
            onConfirm={() => {
              setState({ isConsentModalOpen: false, hasConsented: true });
            }}
            // SO we need to look into this, as onClose should work differently. IF they choose to cancel/no then we should cancel out of the entire sig process
            onClose={e => {
              handleCancel('consent');
              setState({ isConsentModalOpen: false, isSignatureModalOpen: false });
            }}
            dialogTitle="Signature Disclosure Agreement"
            contentText={state.consent}
            isConsent={true}
          />
        )}

        {state.isProcessingSignature && (
          <div className="remote-sign-spinner-container">
            <Spinner
              size={125}
              loadingText={SPINNER_DIALOGS.REMOTE_SIGNATURE}
              lockActions={true}
              textColor="white"
              bgColor="grey"
            />
          </div>
        )}
      </>
    );
  }
};

export default withStyles(styles)(withOrientationChange(RemoteSignatureAuth));
